<template lang="pug">
#app(:data-theme='theme')
  .AsuTokenLoading(v-if='isAsuTokenLoading')
    div
      | Выполняется авторизация по токену АСУ. Пожалуйста подождите
    fade-loader.AsuTokenLoading-spinner(
      :loading='isAsuTokenLoading',
      color='#27bd8a',
      height='8px',
      width='3px',
      radius='10px'
    )
  div(
    v-else,
    :class='{ Main: !isLoginPage }'
  )
    router-view(@continueAsGuest='continueAsGuest')
    helper-panel(
      v-if='helperType',
      :type='helperType'
    )
    error-universal
    .ApiVersion(
      v-if='apiVersion',
      ref='apiVersion',
      :class='{ "ApiVersion--hidden": isHidden }',
      @mouseover='hideVersion'
    )
      div {{ apiVersion.code_last_change_time }}
        span(v-if='$SUB_ENV !== "master" && $SUB_ENV !== "hotfix"') - {{ apiVersion.short_sha }}

  screen-notification(
    v-if='showScreenNotification && !preventShowScreenNotification',
    @continue='preventShowScreenNotification = true'
  )
</template>

<script>
import workspace from '@/mixins/workspace'
import common from '@/mixins/common'
import BlockPopup from '@/components/popups/BlockPopup'
import HelperPanel from '@/components/HelperPanel'
import ErrorUniversal from '@/components/ErrorUniversal'
import ScreenNotification from './components/ScreenNotification'
import { env } from '../config'

/**Минимальная ширина, удобная для работы */
const MIN_NORMAL_WINDOW_WIDTH = 1024

export default {
  name: 'App',

  mixins: [common, workspace],

  components: {
    ScreenNotification,
    HelperPanel,
    ErrorUniversal,
    'fade-loader': () => import('vue-spinner/src/FadeLoader.vue'),
    'screen-notification': () => import('@/components/ScreenNotification.vue')
  },

  data() {
    return {
      showScreenNotification: false,
      preventShowScreenNotification: false,
      versionInterval: null,
      isHidden: false,
      mousePosition: null,
      hiddenTimeout: null,
      isAsuTokenLoading: false
    }
  },

  computed: {
    apiVersion() {
      return this.$store.getters.apiVersion
    },
    newVersionExists() {
      return this.$store.getters.newVersionExists
    },
    isVersionCopying() {
      return this.$store.getters.isVersionCopying
    },
    isWorkspaceCopying() {
      return this.$store.getters.isWorkspaceCopying
    },
    modelingNewWorkspaceCreated() {
      return this.$store.getters.modelingNewWorkspaceCreated
    },
    helperType() {
      return this.$store.getters.helperType
    },
    theme() {
      return this.$store.getters.theme
    },
    isLoginPage() {
      return this.$route.name === 'login'
    }
  },

  watch: {
    newVersionExists(newVal) {
      if (newVal && newVal.length > 0) {
        this.$ui.toast.show({ text: 'Обновилась версия данных', type: 'success' })
        if (newVal.indexOf('admterritory') > -1) {
          this.$store.dispatch('loadWorkspaceSettings', { name: 'centroid' })
        }
        if (newVal.indexOf('zone') > -1) {
          this.$store.dispatch('getZones')
        }
        if (newVal.indexOf('zonegroup') > -1) {
          this.$store.dispatch('getZoneGroups')
        }
        if (newVal.indexOf('containerplatform') > -1) {
          this.$store.dispatch('getContainerTypes')
        }
        if (newVal.indexOf('infrastructure') > -1) {
          this.$store.dispatch('getConvertfunctions')
          this.$store.dispatch('getEconomicModels')
        }
        if (newVal.indexOf('admterritory') > -1 || newVal.indexOf('zonegroup') > -1 || newVal.indexOf('zone') > -1) {
          this.$store.dispatch('loadWorkspaceSettings', { name: 'administrative_levels' })
        }

        if (newVal.indexOf('zonegroup') > -1 || newVal.indexOf('zone') > -1) {
          this.$store.dispatch('getTreatments')
          this.$store.dispatch('loadItems')
        }
      }
    },
    token(newVal) {
      if (!newVal) {
        this.$router.replace('/login')
        clearInterval(this.versionInterval)
        this.versionInterval = null
      } else {
        this.getActiveWorkspaces(false)

        //Получение данных пользователя
        this.$store.dispatch('loadUserData', { force: true })
      }
    },
    isVersionCopying(newVal) {
      if (!newVal) {
        this.$ui.popup.hide()
        this.$store.dispatch('getModelingLimits')
      } else {
        this.$ui.popup.show({
          component: BlockPopup,
          params: {
            text: 'Пожалуйста, подождите. Когда копирование версии будет завершено, это окно закроется автоматически, а вы сможете продолжить работу.',
            title: 'Происходит копирование версии'
          }
        })
      }
    },
    isWorkspaceCopying(newVal, oldVal) {
      if (!newVal) {
        this.$ui.popup.hide()
        this.$store.dispatch('getCopyWorkspaceResult', oldVal).then((r) => {
          if (r.error_code) {
            this.$store.commit('setApiErrorInfo', 'Ошибка копирования рабочего пространсва. ' + r.error_name)
            return
          }
          const newWsId = r.result.new_workspace_id
          if (newWsId) {
            this.$store
              .dispatch('getActiveWorkspaces')
              .then((v) => {
                this.$store.commit('setLastOpenedWorkspace', newWsId)
                this.validateWorkspaceAndInitApp()
              })
              .catch((e) => {
                this.$store.commit('setApiErrorInfo', 'Ошибка получения активных рабочих пространств')
              })
          }
        })
        this.$store.dispatch('getModelingLimits')
      } else {
        this.$ui.popup.show({
          component: BlockPopup,
          params: {
            text: 'Пожалуйста, подождите. Когда копирование рабочего пространства будет завершено, это окно закроется автоматически, а вы сможете продолжить работу.',
            title: 'Происходит копирование рабочего пространства'
          },
          allowCloseOnEscape: false
        })
      }
    },
    workspaceAndVersion(newVal, oldVal) {
      if (oldVal.workspace || oldVal.version) {
        this.$store.dispatch('resetAppBasicDatas')
      }
      if (newVal.version && oldVal.workspace === newVal.workspace) {
        this.$store.dispatch('initApp')
      }
      if (newVal.workspace) {
        this.$store.dispatch('checkVersion')
        this.startRegularCheckVersion()
      } else {
        clearInterval(this.versionInterval)
        this.versionInterval = null
      }
    },

    currentWsItem() {
      if (!this.currentWsItem) {
        window.document.title = env.VUE_APP_DOC_TITLE_PREFIX || 'MW'
        return
      }

      let documentTitle = ''
      if (!!env.VUE_APP_DOC_TITLE_PREFIX) {
        documentTitle = `${env.VUE_APP_DOC_TITLE_PREFIX} - ${this.currentWsItem.name}`
      } else {
        documentTitle = this.currentWsItem.name
      }

      window.document.title = documentTitle
    },

    modelingNewWorkspaceCreated(newVal) {
      if (newVal) {
        this.$ui.toast.show({
          text: 'Создано новое рабочее пространство [' + newVal.workspace + '] ' + newVal.name,
          type: 'success',
          time: 5000
        })
        this.$store.commit('setModelingNewWorkspaceCreated', null)
        this.$store.dispatch('getModelingLimits')
      }
    }
  },

  created() {
    const currentAsuToken = this.$store.getters.asuToken
    const asuToken = this.$route.query.jwt

    //Если есть токен АСУ и он не совпадает с текущим
    if (asuToken && asuToken !== currentAsuToken) {
      //Вход по новому токену АСУ
      this.isAsuTokenLoading = true
      this.$store
        .dispatch('getTokenByAsu', {
          asuToken
        })
        .then(() => {
          //Получение данных пользователя
          this.$store.dispatch('loadUserData')
        })
        .catch(() => {
          this.$ui.toast.show({ text: 'Не удалось авторизоваться по токену АСУ', type: 'error', time: 5000 })
        })
        .finally(() => {
          this.isAsuTokenLoading = false
          this.$router.replace({ path: 'objects', query: {} })
          this.getActiveWorkspaces(false)
        })
    } else if (this.$store.getters.token) {
      //Получение данных пользователя
      this.$store.dispatch('loadUserData')

      if (this.$route.name === 'login') {
        this.$router.replace('objects')
      }
    }
  },

  mounted() {
    if (!this.isLoginPage && !this.isAsuTokenLoading) {
      this.getActiveWorkspaces(false)
    }

    //Если ширина меньше нормальной, то покажем сообщение
    this.showScreenNotification = document.documentElement.clientWidth < MIN_NORMAL_WINDOW_WIDTH
    window.addEventListener('resize', () => {
      this.showScreenNotification = document.documentElement.clientWidth < MIN_NORMAL_WINDOW_WIDTH
    })
  },

  beforeDestroy() {
    clearInterval(this.hiddenInterval)
    document.removeEventListener('mousemove', this.onMouseMove)
  },

  methods: {
    startRegularCheckVersion() {
      if (!this.versionInterval && this.workspace) {
        this.versionInterval = setInterval(() => {
          this.$store.dispatch('checkVersion')
        }, 15000)
      }
    },
    getActiveWorkspaces(allowCloseOnEscape = true) {
      this.$store
        .dispatch('getActiveWorkspaces')
        .then((r) => {
          if (!this.token && this.activeWorkspaceList.length === 1) {
            const ws = this.activeWorkspaceList[0].id
            this.$store.commit('setLastOpenedWorkspace', ws)
            this.selectWorkspace(ws)
          } else {
            this.validateWorkspaceAndInitApp(allowCloseOnEscape)
          }
        })
        .then((v) => {
          console.log('Application was initialized successfully')
        })
        .catch((e) => {
          this.$store.commit('setApiErrorInfo', 'Ошибка получения активных рабочих пространств')
        })
    },
    hideVersion() {
      clearInterval(this.hiddenInterval)
      this.isHidden = true
      if (!this.mousePosition) {
        document.addEventListener('mousemove', this.onMouseMove)
      }
      this.hiddenInterval = setInterval(() => {
        const element = this.$refs.apiVersion.getBoundingClientRect()
        if (this.mousePosition && (this.mousePosition.x < element.x || this.mousePosition.y < element.y)) {
          clearInterval(this.hiddenInterval)
          document.removeEventListener('mousemove', this.onMouseMove)
          this.isHidden = false
          this.mousePosition = null
        }
      }, 500)
    },
    onMouseMove(e) {
      this.mousePosition = {
        x: e.clientX,
        y: e.clientY
      }
    },

    /**
     * Обработка специального события, которое генерирует форма входа
     * при нажатии на кнопку "Продолжить как гость"
     */
    continueAsGuest() {
      this.$router.push('objects').then(() => {
        this.getActiveWorkspaces(false)
      })
    }
  }
}
</script>

<style lang="scss">
@import 'scss/app';
</style>
<style scoped lang="scss">
@import '~base';
#app {
  background: var(--appBg);
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.Main {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0;
  padding-top: 50px;

  & > div:first-child {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 0;
  }
}

.ApiVersion {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 50000;
  padding: 5px 10px;
  background: var(--appBg);
  color: var(--secondaryText);
  font-size: 12px;
  line-height: 14px;

  &--hidden {
    opacity: 0;
    pointer-events: none;
  }
}

.AsuTokenLoading {
  margin: 300px auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  &-spinner {
    margin-top: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue